import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { Navigate, useNavigate } from 'react-router';
import { CenteredPage } from '../app/CenteredPage';
import { Button } from '../components/Button';
import { TextField } from '../components/Field';
import { RoadrunnerMark } from '../components/illustrations';
import { Paper } from '../components/Paper';
import { Loading } from '../components/States';
import { meQueryKey, useMe, useSession } from '../features/auth/session';
import { updateMyProfile } from '../lib/api';
import { errorMessage } from '../lib/errors';
import styles from './Onboarding.module.css';

function suggestedName(metadata: Record<string, unknown> | undefined): string {
  const full = (metadata?.full_name ?? metadata?.name ?? '') as string;
  return full.trim().slice(0, 40);
}

export function OnboardingName() {
  const { data: me, isPending } = useMe();
  const { session } = useSession();
  const [name, setName] = useState(() => suggestedName(session?.user.user_metadata));
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const save = useMutation({
    mutationFn: () => updateMyProfile(name, me?.bio ?? ''),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: meQueryKey });
      navigate('/onboarding/location');
    },
  });

  if (isPending) return <Loading />;
  if (me?.displayName) return <Navigate to={me.location ? '/' : '/onboarding/location'} replace />;

  const submit = (event: FormEvent) => {
    event.preventDefault();
    save.mutate();
  };

  return (
    <CenteredPage>
      <header className={styles.header}>
        <RoadrunnerMark size={64} />
        <p className={styles.steps}>Step 1 of 2</p>
        <h1 className={styles.title}>What should neighbors call you?</h1>
        <p className={styles.lede}>
          Most people use their first name and last initial, like “Maria G.” You can change it later.
        </p>
      </header>
      <Paper as="form" className={styles.form} onSubmit={submit}>
        <TextField
          label="Your name"
          autoComplete="name"
          required
          minLength={2}
          maxLength={40}
          value={name}
          onChange={(e) => setName(e.target.value)}
          error={save.error ? errorMessage(save.error) : undefined}
        />
        <Button type="submit" variant="primary" block disabled={save.isPending || name.trim().length < 2}>
          Continue
        </Button>
      </Paper>
    </CenteredPage>
  );
}
